// services/passkey.ts — thin wrappers around @simplewebauthn/browser.
// Server communication lives in services/authApi.ts; this file is browser-only
// WebAuthn ceremony plumbing.

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
} from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";

/** Does the browser implement the WebAuthn API at all? */
export function isWebAuthnSupported(): boolean {
  return browserSupportsWebAuthn();
}

/** Is there a platform authenticator (Touch ID / Face ID / Windows Hello)? */
export async function hasPlatformAuthenticator(): Promise<boolean> {
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

/**
 * Run the WebAuthn registration ceremony with the options the server provided.
 * Throws Errors with friendly messages on common cancellation/state cases.
 */
export async function registerPasskey(
  options: PublicKeyCredentialCreationOptionsJSON
): Promise<RegistrationResponseJSON> {
  try {
    return await startRegistration({ optionsJSON: options });
  } catch (error: unknown) {
    const err = error as Error & { name?: string };
    if (err.name === "InvalidStateError") {
      throw new Error("This authenticator is already registered with this account.");
    }
    if (err.name === "NotAllowedError") {
      throw new Error("Passkey creation was cancelled or timed out.");
    }
    throw error;
  }
}

/**
 * Run the WebAuthn authentication ceremony with the options the server provided.
 * Throws Error("NO_PASSKEY_FOUND") for the common "user has no passkey on this device" case.
 */
export async function authenticateWithPasskey(
  options: PublicKeyCredentialRequestOptionsJSON
): Promise<AuthenticationResponseJSON> {
  try {
    return await startAuthentication({ optionsJSON: options });
  } catch (error: unknown) {
    const err = error as Error & { name?: string };
    if (err.name === "NotAllowedError" || err.name === "SecurityError") {
      throw new Error("NO_PASSKEY_FOUND");
    }
    throw error;
  }
}

/** Does the browser support conditional UI (passkey autofill)? */
export async function supportsConditionalUI(): Promise<boolean> {
  try {
    return await browserSupportsWebAuthnAutofill();
  } catch {
    return false;
  }
}

/**
 * Start a non-blocking conditional UI authentication. The returned promise
 * resolves when the user picks a passkey from the browser's autofill dropdown.
 * If they never pick one, it stays pending until aborted.
 */
export async function authenticateWithAutofill(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON> {
  return await startAuthentication({
    optionsJSON: options,
    useBrowserAutofill: true,
  });
}
